import { groupBy, sumBy } from 'lodash';

import { assertValue, cnxCompare } from '@libs/utils';
import type {
  CancellationPolicy,
  HotelBookableOptionRoom,
  HotelBookableOptionWithPriceBreakdown,
} from '@libs/trip-planner-interfaces';
import { DateFormat, SemboDate } from '@libs/sembo-dates';

import type { HotelRoom } from '../../modules/hotelDetails/models';
import type { HotelStayTag } from '../../modules/stayTag';
import { HotelStayTagType } from '../../modules/stayTag';

// NOTE: Whole solution is ported from old hotel details with some clean code

export function getHotelStayTag(
  stayIsRefundable: boolean,
  stayRooms:
    | HotelBookableOptionRoom[]
    | HotelRoom[]
    | HotelBookableOptionWithPriceBreakdown[],
  preferredDay: Date
): HotelStayTag | undefined {
  if (!stayIsRefundable) {
    return {
      type: HotelStayTagType.SpecialPrice,
    };
  }

  const sortedCancellationPolicies =
    getCancellationPoliciesSummarySorted(stayRooms);

  if (sortedCancellationPolicies.length === 0) {
    return {
      type: HotelStayTagType.FreeCancellation,
      until: formatDate(preferredDay.toISOString()),
    };
  }

  const firstCancellationPolicyWithCost = sortedCancellationPolicies.find(
    (cancellationPolicy) => cancellationPolicy.amount.amount > 0
  );

  if (!firstCancellationPolicyWithCost) {
    return {
      type: HotelStayTagType.FreeCancellation,
      until: formatDate(preferredDay.toISOString()),
    };
  }

  const isFirstCancellationPolicyWithCostUpcoming = new SemboDate(
    firstCancellationPolicyWithCost.fromDateIso
  ).isAfter(new Date());

  if (isFirstCancellationPolicyWithCostUpcoming) {
    return {
      type: HotelStayTagType.FreeCancellation,
      until: formatDate(firstCancellationPolicyWithCost.fromDateIso),
    };
  }
}

export function formatDate(date: string): string {
  return new SemboDate(date).format(DateFormat.FullDateTime);
}

function getCancellationPoliciesSummarySorted(
  stayRooms:
    | HotelBookableOptionRoom[]
    | HotelRoom[]
    | HotelBookableOptionWithPriceBreakdown[]
): CancellationPolicy[] {
  const allCancellationPolicies = getAllCancellationPolicies(stayRooms);

  return getCancellationPoliciesSummary(allCancellationPolicies);
}

function getAllCancellationPolicies(
  stayRooms:
    | HotelBookableOptionRoom[]
    | HotelRoom[]
    | HotelBookableOptionWithPriceBreakdown[]
): CancellationPolicy[] {
  return stayRooms.flatMap((room) => room.cancellationPolicies);
}

function getCancellationPoliciesSummary(
  allCancellationPolicies: CancellationPolicy[]
): CancellationPolicy[] {
  const groupedByFromDate = groupBy(
    allCancellationPolicies,
    (policy) => policy.fromDate
  );

  return Object.entries(groupedByFromDate)
    .map(([fromDate, groupedPolicies]) => {
      const firstGroupedPolicy = groupedPolicies[0];
      assertValue(
        firstGroupedPolicy,
        `cancellationPoliciesUtils.ts > getCancellationPoliciesSummary > firstGroupedPolicy`
      );

      return {
        fromDate,
        fromDateIso: firstGroupedPolicy.fromDateIso,
        amount: {
          amount: sumBy(groupedPolicies, (policy) => policy.amount.amount),
          currency: firstGroupedPolicy.amount.currency,
          currencySymbol: firstGroupedPolicy.amount.currencySymbol,
        },
      };
    })
    .sort(cnxCompare);
}
